import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import StatusInfo from '../components/StatusInfo';
import { fmtMoney } from '../utils/format';
import useIsMobile from '../hooks/useIsMobile';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const MONTHS = ['', 'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const PAY_LABEL = { pending: 'Ожидает', paid: 'Выплачено', accepted: 'Принято' };
const PAYMENT_STATUS_INFO = [
  ['—', 'Текущий месяц ещё не завершился — статус появится после его окончания.'],
  ['Выплачено', 'Refero перевела вам деньги за эту клинику/сеть и период.'],
  ['Ожидает', 'Либо клиника ещё не оплатила Refero за период, либо оплатила недавно и выплата вам скоро будет проведена.'],
  ['Принято', 'Чек принят Refero (СМЗ) или акт принят вами (ИП) — выплата полностью закрыта.'],
];
const OP_INVOICE_STATUS_INFO = [
  ['Ожидает оплаты клиникой', 'Счёт выставлен клинике/сети — она ещё не перевела Refero деньги по этой операции.'],
  ['Клиника оплатила', 'Клиника/сеть перевела Refero деньги — дальше Refero выплачивает вам (статус в столбце «Документ»).'],
];
const PAGE = 15;

const subtotal = (c) => parseFloat(c.primary_amount || 0) + parseFloat(c.repeat_amount || 0);
const fmt = fmtMoney;

// Общий бейдж-цвет для статуса выплаты (pending/paid/accepted) — переиспользуется
// обеими вкладками.
function payBadgeStyle(status, isPaid, s) {
  if (status === 'accepted') return s.badgeTeal;
  if (status === 'paid' || isPaid) return s.badgeBlue;
  return s.badgeGray;
}

const TAB_VALUES = ['clinics', 'months', 'operations'];

export default function Commissions() {
  const [searchParams] = useSearchParams();
  const initialTab = TAB_VALUES.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'clinics';
  const [tab, setTab] = useState(initialTab); // 'clinics' | 'months' | 'operations'
  return (
    <Layout>
      <h1 style={s.h1}>Комиссии</h1>
      <div style={s.tabs}>
        <button style={{ ...s.tabBtn, ...(tab === 'clinics' ? s.tabBtnActive : {}) }} onClick={() => setTab('clinics')}>По клиникам</button>
        <button style={{ ...s.tabBtn, ...(tab === 'months' ? s.tabBtnActive : {}) }} onClick={() => setTab('months')}>По месяцам</button>
        <button style={{ ...s.tabBtn, ...(tab === 'operations' ? s.tabBtnActive : {}) }} onClick={() => setTab('operations')}>Операции</button>
      </div>
      {tab === 'clinics' ? <ByClinicsTab /> : tab === 'months' ? <ByMonthsTab /> : <OperationsTab />}
    </Layout>
  );
}

function ByClinicsTab() {
  const isMobile = useIsMobile();
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState('');
  const [page, setPage] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const h = { Authorization: 'Bearer ' + localStorage.getItem('token') };
    axios.get(`${API}/commissions/`, { headers: h })
      .then(r => { setRows(r.data); setLoaded(true); })
      .catch(() => { localStorage.clear(); window.location.href = '/login'; });
  }, []);

  const monthOptions = useMemo(() => {
    const set = new Map();
    rows.forEach(c => set.set(`${c.period_year}-${c.period_month}`, `${MONTHS[c.period_month]} ${c.period_year}`));
    return Array.from(set.entries());
  }, [rows]);

  const filtered = useMemo(() => {
    const f = month ? rows.filter(c => `${c.period_year}-${c.period_month}` === month) : rows;
    return [...f].sort((a, b) => (b.period_year - a.period_year) || (b.period_month - a.period_month));
  }, [rows, month]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const pageRows = filtered.slice(page * PAGE, page * PAGE + PAGE);
  const sumPeriod = filtered.reduce((a, c) => a + subtotal(c), 0);

  return (
    <>
      <div style={{ ...s.filterBar, ...(isMobile ? s.filterBarMobile : {}) }}>
        <select style={s.select} value={month} onChange={e => { setMonth(e.target.value); setPage(0); }}>
          <option value="">Все месяцы</option>
          {monthOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div style={s.sumChip}>Итого{month ? ' за месяц' : ' за всё время'}: <b>{fmt(sumPeriod)} ₽</b></div>
      </div>

      <div style={s.tableCard}>
        <div style={isMobile ? s.scrollX : undefined}>
        <div style={{ minWidth: isMobile ? 700 : 'auto' }}>
        <div style={{ ...s.thead, gridTemplateColumns: clinicsCols }}>
          <div>Период</div><div>Клиника / сеть</div><div>Первичные</div><div>Повторные</div><div>Итого</div><div>Квота</div>
          <div>Статус<StatusInfo items={PAYMENT_STATUS_INFO} /></div>
        </div>
        {!loaded ? <div style={s.empty}>Загрузка…</div> : pageRows.length === 0 ? (
          <div style={s.empty}>Комиссий не найдено</div>
        ) : pageRows.map(c => (
          <div key={c.id} style={{ ...s.row, gridTemplateColumns: clinicsCols }}>
            <div>{MONTHS[c.period_month]} {c.period_year}</div>
            <div style={s.strong}>{c.target_name}</div>
            <div>{fmt(c.primary_amount)} ₽</div>
            <div>{fmt(c.repeat_amount)} ₽</div>
            <div style={s.money}>{fmt(subtotal(c))} ₽</div>
            <div>
              {c.monthly_quota != null
                ? <span style={{ ...s.badge, ...(c.quota_fulfilled ? s.badgeTeal : s.badgeGray) }}>{c.quota_count}/{c.monthly_quota}</span>
                : <span style={s.faint}>—</span>}
            </div>
            <div>
              {c.month_ended
                ? <span style={{ ...s.badge, ...payBadgeStyle(c.payment_status, c.is_paid, s) }}>{PAY_LABEL[c.payment_status] || (c.is_paid ? 'Выплачено' : 'Ожидает')}</span>
                : <span style={s.faint}>—</span>}
            </div>
          </div>
        ))}
        </div>
        </div>
      </div>

      {totalPages > 1 && (
        <div style={s.pager}>
          <span>{page * PAGE + 1}–{Math.min((page + 1) * PAGE, filtered.length)} из {filtered.length}</span>
          <div style={s.pagerBtns}>
            <button style={{ ...s.pBtn, ...(page <= 0 ? s.pDis : {}) }} onClick={() => page > 0 && setPage(page - 1)} disabled={page <= 0}>← Назад</button>
            <span style={s.faint}>{page + 1} / {totalPages}</span>
            <button style={{ ...s.pBtn, ...(page >= totalPages - 1 ? s.pDis : {}) }} onClick={() => page < totalPages - 1 && setPage(page + 1)} disabled={page >= totalPages - 1}>Вперёд →</button>
          </div>
        </div>
      )}
    </>
  );
}

const DOC_ACTION_INFO = [
  ['—', 'Текущий месяц ещё не завершился — документа по нему пока нет и не может быть.'],
  ['Загрузить чек', 'Вы самозанятый — после того как Refero перевела деньги, загрузите чек из приложения «Мой налог» по этой сумме.'],
  ['Чек на проверке', 'Чек загружен, Refero его проверяет.'],
  ['Чек отклонён', 'Чек не подошёл — см. причину и загрузите новый.'],
  ['Ждём акт от Refero', 'Вы ИП — Refero перевела деньги, акт-справка ещё готовится.'],
  ['Принять акт', 'Вы ИП — Refero загрузила акт-справку по этой выплате, откройте и примите её (если что-то не так — напишите в техподдержку).'],
  ['Акт принят / Чек принят', 'Документ подтверждён — выплата полностью закрыта.'],
];
// N-120: у «Операций» нет понятия «месяц не завершился» (каждая строка — уже
// закрытое направление) — статус «—» тут физически невозможен, вместо него
// реальное «Ждём оплаты клиники» (документа ещё нет, т.к. клиника не оплатила
// счёт Refero, см. OP_INVOICE_STATUS_INFO).
const OP_DOC_ACTION_INFO = [
  ['Ждём оплаты клиники', 'Клиника/сеть ещё не перевела Refero деньги по этой операции — документ появится только после оплаты.'],
  ['Загрузить чек', 'Вы самозанятый — после того как Refero перевела деньги, загрузите чек из приложения «Мой налог» по этой сумме.'],
  ['Чек на проверке', 'Чек загружен, Refero его проверяет.'],
  ['Чек отклонён', 'Чек не подошёл — см. причину и загрузите новый.'],
  ['Ждём акт от Refero', 'Вы ИП — Refero перевела деньги, акт-справка ещё готовится.'],
  ['Принять акт', 'Вы ИП — Refero загрузила акт-справку по этой выплате, откройте и примите её (если что-то не так — напишите в техподдержку).'],
  ['Акт принят / Чек принят', 'Документ подтверждён — выплата полностью закрыта.'],
];

function ByMonthsTab() {
  const isMobile = useIsMobile();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [modal, setModal] = useState(null); // { year, month, kind, status, reject_reason }
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const headers = { Authorization: 'Bearer ' + localStorage.getItem('token') };

  const load = useCallback(() => {
    axios.get(`${API}/commissions/months`, { headers })
      .then(r => { setRows(r.data); setLoaded(true); })
      .catch(() => setLoaded(true));
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModal = (row) => {
    setModal({ year: row.period_year, month: row.period_month, ...row.document });
    setFile(null); setErr('');
  };
  const closeModal = () => setModal(null);

  const download = async () => {
    try {
      const r = await axios.get(`${API}/commissions/months/document`, { headers, params: { year: modal.year, month: modal.month }, responseType: 'blob' });
      window.open(window.URL.createObjectURL(r.data), '_blank');
    } catch { setErr('Не удалось открыть файл'); }
  };

  const uploadReceipt = async () => {
    if (!file) { setErr('Выберите файл'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/commissions/months/receipt`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        params: { year: modal.year, month: modal.month },
      });
      closeModal(); load();
    } catch (e) { setErr(e?.response?.data?.detail || 'Не удалось загрузить чек'); }
    finally { setBusy(false); }
  };

  const acceptAct = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/commissions/months/accept-act`, {}, { headers, params: { year: modal.year, month: modal.month } });
      closeModal(); load();
    } catch (e) { setErr(e?.response?.data?.detail || 'Не удалось принять акт'); }
    finally { setBusy(false); }
  };

  const actionButton = (row) => {
    const doc = row.document;
    if (!doc) return <span style={s.faint}>—</span>;
    if (doc.kind === 'receipt') {
      if (doc.status === 'accepted') return <button style={s.btnGhostSmall} onClick={() => openModal(row)}>Чек принят</button>;
      if (doc.status === 'pending_review') return <button style={s.btnGhostSmall} onClick={() => openModal(row)}>Чек на проверке</button>;
      if (doc.status === 'rejected') return <button style={s.btnWarnSmall} onClick={() => openModal(row)}>Чек отклонён</button>;
      return <button style={s.btnPaySmall} onClick={() => openModal(row)}>Загрузить чек</button>;
    }
    // act
    if (doc.status === 'accepted') return <button style={s.btnGhostSmall} onClick={() => openModal(row)}>Акт принят</button>;
    if (doc.status === 'sent') return <button style={s.btnPaySmall} onClick={() => openModal(row)}>Принять акт</button>;
    return <span style={s.faint}>Ждём акт от Refero</span>;
  };

  const cols = '1.3fr 1fr 1fr 1fr 1.1fr 1.4fr';

  return (
    <>
      <div style={s.tableCard}>
        <div style={isMobile ? s.scrollX : undefined}>
        <div style={{ minWidth: isMobile ? 640 : 'auto' }}>
        <div style={{ ...s.thead, gridTemplateColumns: cols }}>
          <div>Период</div><div>Первичные</div><div>Повторные</div><div>Итого</div>
          <div>Статус<StatusInfo items={PAYMENT_STATUS_INFO} /></div>
          <div>Документ<StatusInfo items={DOC_ACTION_INFO} /></div>
        </div>
        {!loaded ? <div style={s.empty}>Загрузка…</div> : rows.length === 0 ? (
          <div style={s.empty}>Выплат пока не было</div>
        ) : rows.map(r => (
          <div key={`${r.period_year}-${r.period_month}`} style={{ ...s.row, gridTemplateColumns: cols }}>
            <div style={s.strong}>{MONTHS[r.period_month]} {r.period_year}</div>
            <div>{fmt(r.primary_amount)} ₽</div>
            <div>{fmt(r.repeat_amount)} ₽</div>
            <div style={s.money}>{fmt(r.total)} ₽</div>
            <div>
              {r.month_ended
                ? <span style={{ ...s.badge, ...payBadgeStyle(r.payment_status, r.payment_status !== 'pending', s) }}>{PAY_LABEL[r.payment_status]}</span>
                : <span style={s.faint}>—</span>}
            </div>
            <div>{r.month_ended ? actionButton(r) : <span style={s.faint}>—</span>}</div>
          </div>
        ))}
        </div>
        </div>
      </div>

      {modal && (
        <div style={s.modalOverlay} onClick={closeModal}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>{modal.kind === 'receipt' ? 'Чек' : 'Акт'} — {MONTHS[modal.month]} {modal.year}</h3>
            {err && <div style={s.errText}>{err}</div>}

            {modal.kind === 'receipt' && (
              <>
                {modal.status === 'rejected' && (
                  <p style={s.modalText}>Чек отклонён: «{modal.reject_reason}». Загрузите новый файл.</p>
                )}
                {(modal.status === 'not_uploaded' || modal.status === 'rejected') && (
                  <>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files[0])} style={{ marginTop: 8 }} />
                    <div style={s.modalActions}>
                      <button style={s.btnGhost} onClick={closeModal}>Отмена</button>
                      <button style={s.btn} disabled={busy} onClick={uploadReceipt}>Отправить</button>
                    </div>
                  </>
                )}
                {(modal.status === 'pending_review' || modal.status === 'accepted') && (
                  <>
                    <button style={s.btnGhost} onClick={download}>Скачать чек</button>
                    <p style={{ ...s.modalText, marginTop: 12 }}>
                      {modal.status === 'accepted' ? <span style={{ ...s.badge, ...s.badgeTeal }}>Принято</span> : 'Ожидает проверки Refero.'}
                    </p>
                    <div style={s.modalActions}><button style={s.btnGhost} onClick={closeModal}>Закрыть</button></div>
                  </>
                )}
              </>
            )}

            {modal.kind === 'act' && (
              <>
                <button style={s.btnGhost} onClick={download}>Скачать акт</button>
                {modal.status === 'sent' && (
                  <div style={s.modalActions}>
                    <button style={s.btnGhost} onClick={closeModal}>Отмена</button>
                    <button style={s.btn} disabled={busy} onClick={acceptAct}>Принять</button>
                  </div>
                )}
                {modal.status === 'accepted' && (
                  <>
                    <p style={{ ...s.modalText, marginTop: 12 }}><span style={{ ...s.badge, ...s.badgeTeal }}>Принято</span></p>
                    <div style={s.modalActions}><button style={s.btnGhost} onClick={closeModal}>Закрыть</button></div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// N-115: вкладка «Операции» — отдельный денежный контур, не привязан к календарным
// месяцам (период = окно направления, а не месяц). 1 закрытая операция = 1 счёт
// клинике = 1 чек/акт врачу (не пачками, как «По месяцам»).
const OP_INVOICE_LABEL = { invoiced: 'Ожидает оплаты клиникой', paid: 'Клиника оплатила' };
function opInvoiceBadgeStyle(status, s) {
  return status === 'paid' ? s.badgeBlue : s.badgeGray;
}

function OperationsTab() {
  const isMobile = useIsMobile();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [modal, setModal] = useState(null); // { referral_id, target_name, kind, status, reject_reason }
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const headers = { Authorization: 'Bearer ' + localStorage.getItem('token') };

  const load = useCallback(() => {
    axios.get(`${API}/commissions/operations`, { headers })
      .then(r => { setRows(r.data); setLoaded(true); })
      .catch(() => setLoaded(true));
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModal = (row) => {
    setModal({ referral_id: row.referral_id, target_name: row.target_name, ...row.document });
    setFile(null); setErr('');
  };
  const closeModal = () => setModal(null);

  const download = async () => {
    try {
      const r = await axios.get(`${API}/commissions/operations/${modal.referral_id}/document`, { headers, responseType: 'blob' });
      window.open(window.URL.createObjectURL(r.data), '_blank');
    } catch { setErr('Не удалось открыть файл'); }
  };

  const uploadReceipt = async () => {
    if (!file) { setErr('Выберите файл'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/commissions/operations/${modal.referral_id}/receipt`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      closeModal(); load();
    } catch (e) { setErr(e?.response?.data?.detail || 'Не удалось загрузить чек'); }
    finally { setBusy(false); }
  };

  const acceptAct = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/commissions/operations/${modal.referral_id}/accept-act`, {}, { headers });
      closeModal(); load();
    } catch (e) { setErr(e?.response?.data?.detail || 'Не удалось принять акт'); }
    finally { setBusy(false); }
  };

  const actionButton = (row) => {
    const doc = row.document;
    if (!doc) return <span style={s.faint}>Ждём оплаты клиники</span>;
    if (doc.kind === 'receipt') {
      if (doc.status === 'accepted') return <button style={s.btnGhostSmall} onClick={() => openModal(row)}>Чек принят</button>;
      if (doc.status === 'pending_review') return <button style={s.btnGhostSmall} onClick={() => openModal(row)}>Чек на проверке</button>;
      if (doc.status === 'rejected') return <button style={s.btnWarnSmall} onClick={() => openModal(row)}>Чек отклонён</button>;
      return <button style={s.btnPaySmall} onClick={() => openModal(row)}>Загрузить чек</button>;
    }
    if (doc.status === 'accepted') return <button style={s.btnGhostSmall} onClick={() => openModal(row)}>Акт принят</button>;
    if (doc.status === 'sent') return <button style={s.btnPaySmall} onClick={() => openModal(row)}>Принять акт</button>;
    return <span style={s.faint}>Ждём акт от Refero</span>;
  };

  const cols = '0.9fr 1.6fr 1.4fr 1fr 1fr 1.3fr 1.4fr';

  return (
    <>
      <div style={s.tableCard}>
        <div style={isMobile ? s.scrollX : undefined}>
        <div style={{ minWidth: isMobile ? 820 : 'auto' }}>
        <div style={{ ...s.thead, gridTemplateColumns: cols }}>
          <div>ID направления</div><div>Клиника / сеть</div><div>Период операции</div><div>Сумма оплат</div><div>Комиссия</div>
          <div>Счёт клинике<StatusInfo items={OP_INVOICE_STATUS_INFO} /></div>
          <div>Документ<StatusInfo items={OP_DOC_ACTION_INFO} /></div>
        </div>
        {!loaded ? <div style={s.empty}>Загрузка…</div> : rows.length === 0 ? (
          <div style={s.empty}>Закрытых операций пока не было</div>
        ) : rows.map(r => (
          <div key={r.referral_id} style={{ ...s.row, gridTemplateColumns: cols }}>
            <div style={s.refId}>REF-{r.referral_id}</div>
            <div style={s.strong}>{r.target_name}</div>
            <div style={s.faint}>{new Date(r.window_start).toLocaleDateString('ru')} – {new Date(r.window_end).toLocaleDateString('ru')}</div>
            <div>{fmt(r.total_amount)} ₽</div>
            <div style={s.money}>{fmt(r.doctor_amount)} ₽ <span style={s.faint}>({r.commission_pct}%)</span></div>
            <div><span style={{ ...s.badge, ...opInvoiceBadgeStyle(r.invoice_status, s) }}>{OP_INVOICE_LABEL[r.invoice_status] || r.invoice_status}</span></div>
            <div>{actionButton(r)}</div>
          </div>
        ))}
        </div>
        </div>
      </div>

      {modal && (
        <div style={s.modalOverlay} onClick={closeModal}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>{modal.kind === 'receipt' ? 'Чек' : 'Акт'} — {modal.target_name}</h3>
            {err && <div style={s.errText}>{err}</div>}

            {modal.kind === 'receipt' && (
              <>
                {modal.status === 'rejected' && (
                  <p style={s.modalText}>Чек отклонён: «{modal.reject_reason}». Загрузите новый файл.</p>
                )}
                {(modal.status === 'not_uploaded' || modal.status === 'rejected') && (
                  <>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files[0])} style={{ marginTop: 8 }} />
                    <div style={s.modalActions}>
                      <button style={s.btnGhost} onClick={closeModal}>Отмена</button>
                      <button style={s.btn} disabled={busy} onClick={uploadReceipt}>Отправить</button>
                    </div>
                  </>
                )}
                {(modal.status === 'pending_review' || modal.status === 'accepted') && (
                  <>
                    <button style={s.btnGhost} onClick={download}>Скачать чек</button>
                    <p style={{ ...s.modalText, marginTop: 12 }}>
                      {modal.status === 'accepted' ? <span style={{ ...s.badge, ...s.badgeTeal }}>Принято</span> : 'Ожидает проверки Refero.'}
                    </p>
                    <div style={s.modalActions}><button style={s.btnGhost} onClick={closeModal}>Закрыть</button></div>
                  </>
                )}
              </>
            )}

            {modal.kind === 'act' && (
              <>
                <button style={s.btnGhost} onClick={download}>Скачать акт</button>
                {modal.status === 'sent' && (
                  <div style={s.modalActions}>
                    <button style={s.btnGhost} onClick={closeModal}>Отмена</button>
                    <button style={s.btn} disabled={busy} onClick={acceptAct}>Принять</button>
                  </div>
                )}
                {modal.status === 'accepted' && (
                  <>
                    <p style={{ ...s.modalText, marginTop: 12 }}><span style={{ ...s.badge, ...s.badgeTeal }}>Принято</span></p>
                    <div style={s.modalActions}><button style={s.btnGhost} onClick={closeModal}>Закрыть</button></div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const clinicsCols = '1.3fr 1.8fr 1fr 1fr 1fr 0.9fr 1.1fr';
const s = {
  h1: { color: '#1B2B4B', fontSize: 28, fontWeight: 800, margin: '0 0 20px' },
  tabs: { display: 'flex', gap: 8, marginBottom: 20 },
  tabBtn: { background: '#fff', color: '#64748b', border: '1px solid #d7dee8', padding: '8px 16px', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 600 },
  tabBtnActive: { background: '#1B2B4B', color: '#fff', border: '1px solid #1B2B4B' },
  filterBar: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20 },
  filterBarMobile: { flexWrap: 'wrap' },
  select: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '10px 14px', fontSize: 14, outline: 'none' },
  sumChip: { background: '#e9faf7', color: '#149e90', padding: '8px 16px', borderRadius: 10, fontSize: 14 },
  tableCard: { background: '#fff', borderRadius: 16, padding: '8px 24px', boxShadow: '0 4px 16px rgba(27,43,75,0.06)' },
  scrollX: { overflowX: 'auto' },
  thead: { display: 'grid', padding: '16px 4px 12px', color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #eef2f7' },
  row: { display: 'grid', padding: '14px 4px', borderBottom: '1px solid #f4f7fb', fontSize: 14, color: '#1B2B4B', alignItems: 'center' },
  strong: { fontWeight: 600 },
  refId: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#94a3b8', fontSize: 12 },
  money: { color: '#149e90', fontWeight: 700 },
  badge: { padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, display: 'inline-block' },
  badgeTeal: { background: '#e9faf7', color: '#149e90' },
  badgeGray: { background: '#f1f5f9', color: '#64748b' },
  badgeBlue: { background: '#e0edff', color: '#2563eb' },
  faint: { color: '#94a3b8' },
  empty: { color: '#94a3b8', textAlign: 'center', padding: '32px 0', fontSize: 14 },
  pager: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 4px', color: '#64748b', fontSize: 13 },
  pagerBtns: { display: 'flex', alignItems: 'center', gap: 12 },
  pBtn: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer' },
  pDis: { opacity: 0.4, cursor: 'default' },
  btnPaySmall: { background: '#2EC4B6', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  btnGhostSmall: { background: 'transparent', color: '#64748b', border: '1px solid #d7dee8', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnWarnSmall: { background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { background: '#fff', borderRadius: 16, padding: 24, width: 440, maxWidth: '90vw', boxShadow: '0 10px 30px rgba(27,43,75,0.2)' },
  modalTitle: { color: '#1B2B4B', fontSize: 18, fontWeight: 700, margin: '0 0 12px' },
  modalText: { color: '#475569', fontSize: 13, lineHeight: 1.5, margin: '0 0 12px' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  btn: { background: '#2EC4B6', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', border: '1px solid #d7dee8', color: '#64748b', borderRadius: 10, padding: '10px 20px', fontSize: 14, cursor: 'pointer' },
  errText: { color: '#dc2626', fontSize: 13, marginBottom: 12 },
};
